import * as ngCore from '@angular/core'
import { outputFromObservable } from '@angular/core/rxjs-interop'
import * as ngForms from '@angular/forms'
import * as rxjs from 'rxjs'

import * as _label from '../label'

import { StyleOverrides } from './models'

/**
 * Basic text input.
 *
 *  **Inputs**
 * - {@link AtnDsTextAreaComponent._placeholder|placeholder}?
 * - {@link AtnDsTextAreaComponent._value|value}?
 * - {@link AtnDsTextAreaComponent._disabled|disabled}?
 * - {@link AtnDsTextAreaComponent._label|label}?
 * - {@link AtnDsTextAreaComponent._loading|loading}?
 *
 * **Outputs**
 * - {@link AtnDsTextAreaComponent.change|change}
 *
 * @example
 * ```html
 * <atn-ds-text-area
 *   [placeholder]="'Enter text'"
 *   [loading]="false"
 *   [value]="inputValue"
 *   [disabled]="false"
 *   [label]="'Text Input'"
 *   (change)="handleChange($event)"
 * ></atn-ds-text-area>
 * ```
 */
@ngCore.Component({
  imports: [_label.Component, ngForms.FormsModule],
  selector: 'atn-ds-text-area',
  standalone: true,
  styleUrls: ['./text-area.component.scss'],
  templateUrl: './text-area.component.html',
})
export class AtnDsTextAreaComponent {
  /**
   * @see {@link StyleOverrides}
   */
  public styleOverridesSg = ngCore.input<StyleOverrides | undefined>(
    undefined,
    {
      alias: 'styleOverrides',
    },
  )

  /**
   * Placeholder text
   */
  public readonly placeholderSg = ngCore.input<string | undefined>('', {
    alias: 'placeholder',
  })

  /**
   * Whether the input is loading
   */
  public readonly loadingSg = ngCore.input<boolean | undefined>(false, {
    alias: 'loading',
  })

  /**
   * Input value
   */
  public valueSg = ngCore.model<string | undefined>(undefined, {
    alias: 'value',
  })

  /**
   * Whether the input is disabled
   */
  public disabledSg = ngCore.input<boolean | undefined>(false, {
    alias: 'disabled',
  })

  /**
   * Input label
   */
  public labelSg = ngCore.input<string | undefined>(undefined, {
    alias: 'label',
  })

  /**
   * Whether the input is readonly
   */
  public readonlySg = ngCore.input<boolean | undefined>(false, {
    alias: 'readonly',
  })

  /**
   * Event emitted when the input value changes.
   */
  public readonly change = ngCore.output<string | undefined>()

  /**
   * Workaround from {@link https://github.com/angular/angular/issues/54837#issuecomment-2043052265}
   */
  public readonly submit$ = new rxjs.Subject<string | undefined>()
  /**
   * Indicates that the user has pressed enter, in order to submit the text
   */
  public readonly submit = outputFromObservable(this.submit$)

  public onChangeHandler(event: Event): void {
    event.stopPropagation()
    const target = event.target as HTMLInputElement | null
    this.change.emit(target?.value?.toString() ?? undefined)
  }

  public onEnterHandler(event: Event): void {
    // When the SHIFT key is held or we don't have a submit listener, we just
    // let the enter be treated normally
    if (!(event as KeyboardEvent).shiftKey && this.submit$.observed) {
      // Otherwise, we prevent anything from being added to the value
      event.preventDefault()
      const value = this.valueSg()
      // And we emit a submit event
      this.submit$.next(value)
    }
  }
}
